import { useQueryClient } from "@tanstack/react-query";
import { CompassIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, Navigate, useLocation } from "react-router";
import { EmptyState } from "@/components/common/states.tsx";
import { Button } from "@/components/ui/button.tsx";
import { useI18n } from "@/i18n/index.tsx";
import { queryKeys } from "@/lib/api.ts";
import { isMockTransport, request } from "@/lib/http.ts";
import { fetchMe } from "@/lib/queries.ts";
import { FullPageLoader } from "./guards.tsx";

export function NotFoundPage() {
  const { t } = useI18n();
  return (
    <div className="flex min-h-dvh items-center justify-center bg-background">
      <EmptyState
        icon={CompassIcon}
        title={t.errors.notFoundTitle}
        text={t.errors.notFoundText}
        action={
          <Button asChild variant="outline" size="sm">
            <Link to="/">{t.errors.toApp}</Link>
          </Button>
        }
      />
    </div>
  );
}

/**
 * `/auth/google` and `/auth/dev` are server routes. Only with fixture data (no server) do they reach
 * the page; then the fake sign-in is completed here.
 */
export function MockAuthRoute() {
  const location = useLocation();
  const queryClient = useQueryClient();
  const enabled = isMockTransport();
  const [done, setDone] = useState(false);
  const target = `${location.pathname}${location.search}`;

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    void (async () => {
      await request("POST", target).catch(() => null);
      const me = await fetchMe().catch(() => null);
      queryClient.setQueryData(queryKeys.me, me);
      if (!cancelled) setDone(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, target, queryClient]);

  if (!enabled) return <NotFoundPage />;
  if (!done) return <FullPageLoader />;
  return <Navigate to="/" replace />;
}
